<template>
  <div v-loading="refreshing" class="photos-container">
    <div class="photo-header">
      <div class="header-left">
        <span class="title">相册</span>
        <span v-if="isEditMode" class="selection-info">已选中 {{ selectedIds.length }} 项</span>
      </div>
      <div class="header-right">
        <el-button
          :type="isEditMode ? 'default' : 'primary'"
          size="small"
          @click="toggleEditMode"
        >
          {{ isEditMode ? '取消管理' : '批量管理' }}
        </el-button>
      </div>
    </div>

    <div
      v-infinite-scroll="onLoad"
      class="scroll-wrapper"
      :infinite-scroll-disabled="disabled"
    >
      <div v-for="group in groupedPhotos" :key="group.date" class="photo-group">
        <div class="group-header">
          <span class="date-title">{{ formatGroupDate(group.date) }}</span>
          <el-checkbox
            v-if="isEditMode"
            :value="isGroupSelected(group)"
            @change="selectGroup(group)"
          >
            全选
          </el-checkbox>
        </div>

        <div class="photo-grid">
          <div
            v-for="item in group.children"
            :key="item.fileId"
            class="photo-item"
            :class="{ 'is-selected': selectedIds.includes(item.fileId) }"
            @click="onItemClick(item)"
          >
            <el-image
              class="image-content"
              :src="item.fileType === 1002 ? require('@/assets/img/video.jpg') : item.url"
              fit="cover"
              lazy
            />

            <div v-if="item.fileType === 1002" class="video-badge">
              <i class="el-icon-video-play" />
              <span>{{ item.duration || '视频' }}</span>
            </div>

            <div v-if="isEditMode" class="select-mask">
              <div class="check-circle">
                <i v-if="selectedIds.includes(item.fileId)" class="el-icon-check" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="load-status">
        <p v-if="loading"><i class="el-icon-loading" /> 加载中...</p>
        <p v-if="finished && photoList.length > 0">没有更多照片了</p>
        <el-empty v-if="finished && photoList.length === 0" description="相册空空如也" />
      </div>
    </div>

    <transition name="el-zoom-in-bottom">
      <div v-if="isEditMode && selectedIds.length > 0" class="batch-footer">
        <div class="footer-content">
          <el-button type="primary" icon="el-icon-download" @click="handleBatchDownload">下载</el-button>
          <el-button type="danger" icon="el-icon-delete" @click="handleBatchDelete">删除</el-button>
        </div>
      </div>
    </transition>

    <el-dialog
      :visible.sync="showPlayerModal"
      width="800px"
      custom-class="video-dialog"
      append-to-body
      @closed="onVideoPopupClosed"
    >
      <div v-if="currentVideoItem" class="video-container">
        <video
          ref="videoPlayer"
          :src="currentVideoItem.url"
          controls
          class="full-video"
        />
        <div class="video-detail">
          <h3>{{ currentVideoItem.filename }}</h3>
          <p>更新时间：{{ currentVideoItem.updateTime }}</p>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getPhotoItems } from '@/api/disk'

export default {
  name: 'DiskPhoto',
  data() {
    return {
      photoList: [],
      loading: false,
      finished: false,
      refreshing: false,
      isEditMode: false,
      selectedIds: [],
      queryParams: {
        pn: 1
      },
      showPlayerModal: false,
      currentVideoItem: null
    }
  },
  computed: {
    groupedPhotos() {
      return this.groupDataByDate(this.photoList)
    },
    disabled() {
      return this.loading || this.finished
    }
  },
  methods: {
    async onLoad() {
      if (this.finished) return
      this.loading = true

      try {
        const resp = await getPhotoItems(this.queryParams)
        if (resp.code === 0) {
          const list = resp.data.list || []
          this.photoList = [...this.photoList, ...list]

          if (!resp.data.hasNext) {
            this.finished = true
          } else {
            this.queryParams.pn++
          }
        } else {
          this.finished = true
          this.$message.error(resp.msg)
        }
      } catch (e) {
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },

    groupDataByDate(flatList) {
      const groups = {}
      flatList.forEach(item => {
        const date = item.updateTime.split(' ')[0]
        if (!groups[date]) groups[date] = []
        groups[date].push(item)
      })
      return Object.keys(groups).map(date => ({
        date,
        children: groups[date]
      })).sort((a, b) => new Date(b.date) - new Date(a.date))
    },

    formatGroupDate(dateStr) {
      const d = new Date(dateStr)
      const now = new Date()
      if (dateStr === now.toISOString().split('T')[0]) return '今天'
      return `${d.getMonth() + 1}月${d.getDate()}日`
    },

    onItemClick(item) {
      if (this.isEditMode) {
        this.handleSelect(item.fileId)
      } else {
        if (item.fileType === 1001) {
          const imageUrls = []
          imageUrls.push({
            src: item.url,
            alt: item.filename
          })
          this.$viewerApi({
            images: imageUrls,
            options: {
              initialViewIndex: 0,
              movable: true,
              fullscreen: false,
              keyboard: true
            }
          })
        } else if (item.fileType === 1002) {
          this.currentVideoItem = item
          this.showPlayerModal = true
        }
      }
    },

    handleSelect(id) {
      const idx = this.selectedIds.indexOf(id)
      idx > -1 ? this.selectedIds.splice(idx, 1) : this.selectedIds.push(id)
    },

    toggleEditMode() {
      this.isEditMode = !this.isEditMode
      this.selectedIds = []
    },

    selectGroup(group) {
      const groupIds = group.children.map(i => i.fileId)
      if (this.isGroupSelected(group)) {
        this.selectedIds = this.selectedIds.filter(id => !groupIds.includes(id))
      } else {
        groupIds.forEach(id => {
          if (!this.selectedIds.includes(id)) this.selectedIds.push(id)
        })
      }
    },

    isGroupSelected(group) {
      return group.children.every(i => this.selectedIds.includes(i.fileId))
    },

    onVideoPopupClosed() {
      if (this.$refs.videoPlayer) this.$refs.videoPlayer.pause()
    }

    // ... 其他业务逻辑 (handleBatchDelete 等)
  }
}
</script>

<style scoped>
.photos-container {
  padding: 20px;
  background: #fff;
  border-radius: 12px;
  min-height: 80vh;

  .photo-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    .title { font-size: 20px; font-weight: bold; }
    .selection-info { margin-left: 15px; color: #909399; font-size: 14px; }
  }

  .group-header {
    margin: 25px 0 15px;
    display: flex;
    align-items: center;
    .date-title { font-size: 16px; font-weight: 600; margin-right: 15px; }
  }

  .photo-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }

  .photo-item {
    position: relative;
    aspect-ratio: 1 / 1;
    cursor: pointer;
    border-radius: 8px;
    overflow: hidden;
    transition: transform 0.2s;

    &:hover { transform: scale(1.02); }

    .image-content { width: 100%; height: 100%; }

    &.is-selected {
      border: 3px solid #409EFF;
    }
  }

  .video-badge {
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: rgba(0,0,0,0.6);
    color: #fff;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    display: flex;
    align-items: center;
    i { margin-right: 4px; }
  }

  .select-mask {
    position: absolute;
    top: 8px;
    right: 8px;
    .check-circle {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: rgba(255,255,255,0.8);
      border: 1px solid #ccc;
      display: flex;
      align-items: center;
      justify-content: center;
      i { color: #409EFF; font-weight: bold; }
    }
  }

  .batch-footer {
    position: fixed;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    background: #fff;
    padding: 12px 40px;
    border-radius: 50px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
    z-index: 2000;
  }
}

.video-container {
  .full-video { width: 100%; border-radius: 8px; }
  .video-detail { margin-top: 15px; }
}
</style>
